import {
  type UserQuestDetail,
  UserQuestProgress,
} from "Infrastructure/Api/Api";
import { SideDrawer } from "Infrastructure/Components/Drawers/SideDrawer";
import { formatDate } from "Infrastructure/Helpers/dateFormatters";
import { useDateFnsFormatString } from "Infrastructure/Hooks/DateFns/useDateFnsFormatString";
import { useRequestStatus } from "Infrastructure/Hooks/useRequestStatus";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useUserQuestMutation } from "Requests/Queries/useQuestRequestMutations";
import { Box, Stack, useTheme } from "@mui/material";
import {
  CalendarCheck2,
  CalendarClock,
  CalendarPlus2,
  Coins,
  Tag,
  Type,
  User,
} from "lucide-react";
import type { FC } from "react";
import { DetailField } from "../ColumnCards/DetailField";
import { ActionButtons } from "../ColumnCards/Shared/ActionButtons";
import { RewardBadge } from "../ColumnCards/Shared/RewardBadge";
import { StatusChip } from "../ColumnCards/Shared/StatusChip";
import { UserChip } from "../ColumnCards/Shared/UserChip";

interface RequestDetailViewProps {
  request: UserQuestDetail | null;
  open: boolean;
  onClose: () => void;
}

export const RequestDetailCard: FC<RequestDetailViewProps> = ({
  request,
  open,
  onClose,
}) => {
  const { customSpacing } = useTheme();
  const { t } = useResource();
  const dateFormat = useDateFnsFormatString();
  const statusConfig = useRequestStatus(
    request?.progress || UserQuestProgress.Requested,
  );

  const mutation = useUserQuestMutation();

  if (!request) {
    return null;
  }

  const handleApprove = () => {
    if (request.progress === UserQuestProgress.Requested) {
      mutation.mutate(
        {
          userQuestID: request.userQuestID,
          questID: request.questID,
          progress: UserQuestProgress.Active,
          dateStart: new Date().toISOString(),
        },
        { onSuccess: onClose },
      );
    } else if (request.progress === UserQuestProgress.Pending) {
      mutation.mutate(
        {
          userQuestID: request.userQuestID,
          questID: request.questID,
          progress: UserQuestProgress.Completed,
          dateStart: request.dateStart,
          dateCompleted: request.dateCompleted || new Date().toISOString(),
        },
        { onSuccess: onClose },
      );
    }
  };

  const handleReject = () => {
    mutation.mutate(
      {
        userQuestID: request.userQuestID,
        questID: request.questID,
        progress: UserQuestProgress.Canceled,
      },
      { onSuccess: onClose },
    );
  };

  const handleSubmitCompletion = () => {
    mutation.mutate(
      {
        userQuestID: request.userQuestID,
        questID: request.questID,
        progress: UserQuestProgress.Pending,
        dateStart: request.dateStart,
        dateCompleted: null,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={t(Resources.RequestCards.RequestDetail.Title)}
    >
      <Stack sx={{ flex: 1 }} spacing={customSpacing.md}>
        <Box sx={{ flex: 1, overflowY: "auto", pr: customSpacing.xs }}>
          <Stack spacing={customSpacing.md}>
            <DetailField
              icon={User}
              label={t(Resources.RequestCards.RequestDetail.Fields.RequestedBy)}
            >
              <UserChip name={request.userName} />
            </DetailField>

            <DetailField
              icon={Type}
              label={t(Resources.RequestCards.RequestDetail.Fields.Quest)}
              value={request.questName}
            />

            <Stack direction="row" spacing={customSpacing.md}>
              <DetailField
                icon={Coins}
                label={t(Resources.RequestCards.RequestDetail.Fields.Reward)}
                flex={1}
              >
                <RewardBadge value={request.rewardCoins} absolute={false} />
              </DetailField>

              <DetailField
                icon={Tag}
                label={t(Resources.RequestCards.RequestDetail.Fields.Status)}
                flex={1}
              >
                <StatusChip status={statusConfig} />
              </DetailField>
            </Stack>

            <DetailField
              icon={CalendarPlus2}
              label={t(Resources.RequestCards.RequestDetail.Fields.CreatedDate)}
              value={formatDate(request.dateStart, dateFormat)}
            />

            {request.dateStart &&
              request.progress !== UserQuestProgress.Requested && (
                <DetailField
                  icon={CalendarCheck2}
                  label={t(
                    Resources.RequestCards.RequestDetail.Fields.ApprovedDate,
                  )}
                  value={formatDate(request.dateStart, dateFormat)}
                />
              )}

            {request.dateCompleted && (
              <DetailField
                icon={CalendarClock}
                label={t(
                  Resources.RequestCards.RequestDetail.Fields.CompletedDate,
                )}
                value={formatDate(request.dateCompleted, dateFormat)}
              />
            )}
          </Stack>
        </Box>

        <ActionButtons
          status={
            request.progress.toLowerCase() as
              | "requested"
              | "active"
              | "pending"
              | "completed"
              | "canceled"
          }
          onClose={onClose}
          onApprove={handleApprove}
          onReject={handleReject}
          onSubmitCompletion={handleSubmitCompletion}
          isLoading={mutation.isPending}
        />
      </Stack>
    </SideDrawer>
  );
};
